import React, { useRef, useState, useEffect, useCallback } from 'react';
import authService from '../services/AuthService';

const API_BASE = 'https://server.multisellerpro.com/api';
const CHUNK_INTERVAL_MS = 6000;
const MIN_PART_BYTES = 5.1 * 1024 * 1024;

const fmt = (s) =>
    `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

export default function VideoOverdub({ review, onClose, onSaved }) {

    const [phase, setPhase] = useState('loading');
    const [videoBlobUrl, setVideoBlobUrl] = useState('');
    const [uploadMeta, setUploadMeta] = useState(null);
    const [countdown, setCountdown] = useState(3);
    const [elapsed, setElapsed] = useState(0);
    const [partsUploaded, setPartsUploaded] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const [previewBlobUrl, setPreviewBlobUrl] = useState('');

    const videoRef = useRef(null);
    const previewRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const timerRef = useRef(null);
    const partsRef = useRef([]);
    const partNumRef = useRef(1);
    const accBlobsRef = useRef([]);
    const accSizeRef = useRef(0);
    const queueRef = useRef([]);
    const uploadingRef = useRef(false);
    const stoppedRef = useRef(false);
    const finalisedRef = useRef(false);
    const allChunksRef = useRef([]);
    const uploadMetaRef = useRef(null);

    const fetchPresign = useCallback(async () => {
        const r = await authService.authenticatedFetch(`${API_BASE}/video/overdub-presign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3_key: review.mp4_key || review.s3_key }),
        });
        if (!r.ok) throw new Error();
        return r.json();
    }, [review.s3_key]);

    // ── init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const { play_url, new_key, upload_id } = await fetchPresign();
                const meta = { new_key, upload_id };
                setUploadMeta(meta);
                uploadMetaRef.current = meta;

                // fetch as blob so captureStream() works (avoids cross-origin block)
                const res = await fetch(play_url);
                const blob = await res.blob();
                setVideoBlobUrl(URL.createObjectURL(blob));

                setPhase('idle');
            } catch {
                setErrorMsg('Failed to load video. Please try again.');
                setPhase('error');
            }
        })();
        return () => {
            streamRef.current?.getTracks().forEach(t => t.stop());
            clearInterval(timerRef.current);
        };
    }, []);

    // cleanup blob URLs
    useEffect(() => () => { if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl); }, [videoBlobUrl]);
    useEffect(() => () => { if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl); }, [previewBlobUrl]);

    // ── upload helpers ────────────────────────────────────────────────────────
    const apiPresign = async (upload_id, key, part_number) => {
        const r = await authService.authenticatedFetch(`${API_BASE}/video/presign-part`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upload_id, key, part_number }),
        });
        if (!r.ok) throw new Error('presign-part failed');
        return r.json();
    };

    const apiComplete = async (upload_id, key, parts) => {
        const r = await authService.authenticatedFetch(`${API_BASE}/video/complete-upload`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upload_id, key, parts }),
        });
        if (!r.ok) throw new Error('complete-upload failed');
        return r.json();
    };

    const finalise = useCallback(async () => {
        if (finalisedRef.current) return;
        finalisedRef.current = true;
        try {
            const { upload_id, new_key } = uploadMetaRef.current;
            await apiComplete(upload_id, new_key, partsRef.current);
            const mimeType = recorderRef.current?.mimeType || 'video/webm';
            const blob = new Blob(allChunksRef.current, { type: mimeType });
            setPreviewBlobUrl(URL.createObjectURL(blob));
            setPhase('preview');
        } catch {
            finalisedRef.current = false;
            setPhase('error');
            setErrorMsg('Failed to finalise. Please try again.');
        }
    }, []);

    const drainQueue = useCallback(async () => {
        if (uploadingRef.current) return;
        uploadingRef.current = true;
        while (queueRef.current.length > 0) {
            const blob = queueRef.current.shift();
            const pn = partNumRef.current++;
            const { upload_id, new_key } = uploadMetaRef.current;
            let ok = false;
            for (let attempt = 0; attempt < 3 && !ok; attempt++) {
                try {
                    const { presigned_url } = await apiPresign(upload_id, new_key, pn);
                    const s3r = await fetch(presigned_url, {
                        method: 'PUT', body: blob, headers: { 'Content-Type': '' },
                    });
                    if (!s3r.ok) throw new Error();
                    partsRef.current.push({ PartNumber: pn, ETag: s3r.headers.get('ETag') });
                    setPartsUploaded(partsRef.current.length);
                    ok = true;
                } catch {
                    if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
                }
            }
            if (!ok) {
                setPhase('error');
                setErrorMsg('Upload failed. Please try again.');
                uploadingRef.current = false;
                return;
            }
        }
        uploadingRef.current = false;
        if (stoppedRef.current && queueRef.current.length === 0) finalise();
    }, [finalise]);

    const flushAccumulator = useCallback((isFinal = false) => {
        if (accBlobsRef.current.length === 0) return;
        if (!isFinal && accSizeRef.current < MIN_PART_BYTES) return;
        const mimeType = recorderRef.current?.mimeType || 'video/webm';
        const blob = new Blob(accBlobsRef.current, { type: mimeType });
        accBlobsRef.current = [];
        accSizeRef.current = 0;
        queueRef.current.push(blob);
        drainQueue();
    }, [drainQueue]);

    // ── recording ─────────────────────────────────────────────────────────────
    const beginCountdown = () => {
        setPhase('countdown');
        setCountdown(3);
        let c = 3;
        const id = setInterval(() => {
            c--;
            setCountdown(c);
            if (c === 0) { clearInterval(id); startRecording(); }
        }, 1000);
    };

    const startRecording = async () => {
        partsRef.current = [];
        partNumRef.current = 1;
        accBlobsRef.current = [];
        accSizeRef.current = 0;
        queueRef.current = [];
        allChunksRef.current = [];
        uploadingRef.current = false;
        stoppedRef.current = false;
        finalisedRef.current = false;
        setElapsed(0);
        setPartsUploaded(0);
        setErrorMsg('');
        if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(''); }

        let micStream;
        try {
            micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            streamRef.current = micStream;
        } catch {
            setPhase('error');
            setErrorMsg('Microphone access denied.');
            return;
        }

        const vid = videoRef.current;
        vid.currentTime = 0;
        vid.muted = true;
        await vid.play();

        setPhase('recording');

        // combine video track from <video> element + mic audio
        const audioCtx = new AudioContext();
        const micSource = audioCtx.createMediaStreamSource(micStream);
        const dest = audioCtx.createMediaStreamDestination();
        micSource.connect(dest);

        const videoTrack = vid.captureStream().getVideoTracks()[0];
        const combined = new MediaStream([
            videoTrack,
            ...dest.stream.getAudioTracks(),
        ]);

        timerRef.current = setInterval(() => {
            setElapsed(prev => {
                if (vid && prev + 1 >= Math.floor(vid.duration || 999)) stopRecording();
                return prev + 1;
            });
        }, 1000);

        const mimeType =
            ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
                .find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';

        const recorder = new MediaRecorder(combined, { mimeType, videoBitsPerSecond: 2_500_000 });
        recorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (!e.data?.size || stoppedRef.current) return;
            allChunksRef.current.push(e.data);
            accBlobsRef.current.push(e.data);
            accSizeRef.current += e.data.size;
            flushAccumulator(false);
        };

        recorder.onstop = () => {
            clearInterval(timerRef.current);
            stoppedRef.current = true;
            micStream.getTracks().forEach(t => t.stop());
            audioCtx.close();
            vid.pause();
            vid.muted = false;
            setPhase('stopping');
            flushAccumulator(true);
            if (queueRef.current.length === 0 && !uploadingRef.current) finalise();
        };

        recorder.start(CHUNK_INTERVAL_MS);
    };

    const stopRecording = () => {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        clearInterval(timerRef.current);
    };

    // ── commit ────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        setPhase('saving');
        try {
            const r = await authService.authenticatedFetch(`${API_BASE}/video/overdub-commit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    review_id: review.id,
                    original_key: review.mp4_key || review.s3_key,
                    new_key: uploadMetaRef.current.new_key,
                }),
            });
            if (!r.ok) throw new Error();
            const { s3_url } = await r.json();
            setPhase('saved');
            onSaved?.(s3_url);
        } catch {
            setPhase('error');
            setErrorMsg('Failed to save. Please try again.');
        }
    };

    const handleReRecord = async () => {
        setPhase('loading');
        try {
            const { play_url, new_key, upload_id } = await fetchPresign();
            const meta = { new_key, upload_id };
            setUploadMeta(meta);
            uploadMetaRef.current = meta;
            // re-fetch blob so captureStream works again
            const res = await fetch(play_url);
            const blob = await res.blob();
            if (videoBlobUrl) URL.revokeObjectURL(videoBlobUrl);
            setVideoBlobUrl(URL.createObjectURL(blob));
            partsRef.current = [];
            partNumRef.current = 1;
            finalisedRef.current = false;
            stoppedRef.current = false;
            setElapsed(0);
            setPartsUploaded(0);
            setErrorMsg('');
            setPhase('idle');
        } catch {
            setErrorMsg('Failed to reset. Please try again.');
            setPhase('error');
        }
    };

    const videoDuration = videoRef.current?.duration || 0;
    const pct = videoDuration > 0 ? Math.min((elapsed / videoDuration) * 100, 100) : 0;

    return (
        <div style={s.overlay}>
            <div style={s.card}>

                <div style={s.header}>
                    <span style={s.title}>
                        🎙 Re-record Audio —{' '}
                        <em style={{ fontWeight: 400, fontSize: '11px' }}>
                            {review.review_title || review.product_title}
                        </em>
                    </span>
                    <button style={s.closeBtn} onClick={onClose}>✕</button>
                </div>

                {/* Loading state */}
                {phase === 'loading' && (
                    <div style={s.videoWrap}>
                        <div style={s.spinnerWrap}>
                            <div style={s.spinner} />
                            <p style={s.spinnerLabel}>Loading video…</p>
                        </div>
                    </div>
                )}

                {/* Original video — idle / countdown / recording / stopping */}
                {['idle', 'countdown', 'recording', 'stopping'].includes(phase) && videoBlobUrl && (
                    <div style={s.videoWrap}>
                        <video
                            ref={videoRef}
                            src={videoBlobUrl}
                            style={s.video}
                            playsInline
                            controls={phase === 'idle'}
                            muted={phase !== 'idle'}
                        />
                        {phase === 'countdown' && (
                            <div style={s.cdOverlay}>
                                <span style={s.cdNum}>{countdown}</span>
                            </div>
                        )}
                        {phase === 'recording' && (
                            <div style={s.recBadge}>
                                <span style={s.recDot} />
                                🎙 {fmt(elapsed)}
                            </div>
                        )}
                    </div>
                )}

                {/* Preview — combined video + new audio */}
                {phase === 'preview' && previewBlobUrl && (
                    <div style={s.videoWrap}>
                        <video
                            ref={previewRef}
                            src={previewBlobUrl}
                            style={s.video}
                            playsInline
                            controls
                            autoPlay
                        />
                    </div>
                )}

                {/* Saving */}
                {phase === 'saving' && (
                    <div style={s.videoWrap}>
                        <div style={s.spinnerWrap}>
                            <div style={s.spinner} />
                            <p style={s.spinnerLabel}>Saving…</p>
                        </div>
                    </div>
                )}

                {/* Progress bar */}
                {phase === 'recording' && (
                    <div style={s.progressWrap}>
                        <div style={{ ...s.progressBar, width: `${pct}%` }} />
                    </div>
                )}

                {/* Status */}
                {(phase === 'recording' || phase === 'stopping') && (
                    <div style={s.statusLine}>
                        {partsUploaded === 0
                            ? 'Buffering…'
                            : `✓ ${partsUploaded} chunk${partsUploaded !== 1 ? 's' : ''} uploaded`}
                        {phase === 'stopping' && ' — finalising…'}
                    </div>
                )}

                {/* Banners */}
                {phase === 'idle' && (
                    <div style={s.infoBanner}>
                        👆 Preview the original video above, then hit <strong>Start Recording</strong>.<br />
                        The video will replay with your mic audio replacing the original.
                    </div>
                )}
                {phase === 'preview' && (
                    <div style={s.infoBanner}>
                        ✅ This is exactly what will be saved — video + your new audio. Happy with it?
                    </div>
                )}

                {/* Saved */}
                {phase === 'saved' && (
                    <div style={s.savedWrap}>
                        <div style={s.doneCheck}>✓</div>
                        <p style={s.savedLabel}>Video replaced successfully!</p>
                        <button style={{ ...s.btn, ...s.btnBlue }} onClick={onClose}>Done</button>
                    </div>
                )}

                {/* Error */}
                {phase === 'error' && <div style={s.errBox}>{errorMsg}</div>}

                {/* Controls */}
                {phase !== 'saved' && (
                    <div style={s.controls}>
                        {phase === 'idle' && (
                            <button style={{ ...s.btn, ...s.btnRed }} onClick={beginCountdown}>
                                ⬤ Start Recording
                            </button>
                        )}
                        {phase === 'recording' && (
                            <button style={{ ...s.btn, ...s.btnGray }} onClick={stopRecording}>
                                ■ Stop
                            </button>
                        )}
                        {phase === 'preview' && (
                            <>
                                <button style={{ ...s.btn, ...s.btnTeal }} onClick={handleSave}>
                                    ✓ Save & Replace
                                </button>
                                <button style={{ ...s.btn, ...s.btnGray }} onClick={handleReRecord}>
                                    ↺ Re-record
                                </button>
                            </>
                        )}
                        {phase === 'error' && (
                            <button style={{ ...s.btn, ...s.btnGray }} onClick={handleReRecord}>
                                ↺ Try Again
                            </button>
                        )}
                    </div>
                )}

            </div>
            <style>{`
                @keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
                @keyframes pop{0%{transform:scale(.7);opacity:0}100%{transform:scale(1);opacity:1}}
                @keyframes spin{to{transform:rotate(360deg)}}
            `}</style>
        </div>
    );
}

const s = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', padding: '16px',
    },
    card: {
        background: 'linear-gradient(145deg,#0f172a 0%,#1e293b 100%)',
        borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 32px 64px rgba(0,0,0,0.6)',
        width: '100%', maxWidth: '520px', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflowY: 'auto',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)',
    },
    title: { color: 'white', fontWeight: '700', fontSize: '13px', fontFamily: 'sans-serif' },
    closeBtn: {
        background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
        width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer', fontSize: '12px',
    },
    videoWrap: {
        position: 'relative', width: '100%', height: '384px',
        background: '#000', overflow: 'hidden', flexShrink: 0,
    },
    video: { width: '100%', height: '100%', objectFit: 'contain', display: 'block' },
    cdOverlay: {
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    cdNum: {
        fontSize: '96px', fontWeight: '900', color: 'white',
        fontFamily: 'sans-serif', lineHeight: 1, animation: 'pop .3s ease',
    },
    recBadge: {
        position: 'absolute', top: '8px', left: '8px',
        background: 'rgba(0,0,0,0.6)', color: 'white', padding: '3px 8px',
        borderRadius: '20px', fontSize: '11px', fontWeight: '600', fontFamily: 'monospace',
        display: 'flex', alignItems: 'center', gap: '5px',
    },
    recDot: {
        width: '7px', height: '7px', borderRadius: '50%',
        background: '#f43f5e', animation: 'blink 1s infinite',
    },
    spinnerWrap: {
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: '12px',
    },
    spinner: {
        width: '36px', height: '36px', borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.15)',
        borderTop: '3px solid #14b8a6',
        animation: 'spin 0.8s linear infinite',
    },
    spinnerLabel: { color: 'white', fontFamily: 'sans-serif', fontSize: '13px', margin: 0 },
    progressWrap: { height: '3px', background: 'rgba(255,255,255,0.08)', flexShrink: 0 },
    progressBar: { height: '3px', background: '#14b8a6', transition: 'width 1s linear' },
    statusLine: {
        padding: '6px 16px', fontSize: '11px', fontFamily: 'sans-serif',
        color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.03)',
    },
    infoBanner: {
        margin: '12px 16px', padding: '10px 14px',
        background: 'rgba(255,255,255,0.05)', borderRadius: '10px',
        color: 'rgba(255,255,255,0.7)', fontSize: '12px',
        fontFamily: 'sans-serif', lineHeight: '1.6',
    },
    errBox: {
        margin: '8px 16px', background: 'rgba(244,63,94,0.12)',
        border: '1px solid rgba(244,63,94,0.35)', color: '#fda4af',
        borderRadius: '8px', padding: '8px 12px', fontSize: '12px', fontFamily: 'sans-serif',
    },
    controls: {
        display: 'flex', gap: '8px', padding: '12px 16px',
        justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0,
    },
    btn: {
        padding: '9px 22px', borderRadius: '50px', border: 'none',
        fontWeight: '700', fontSize: '12px', cursor: 'pointer', fontFamily: 'sans-serif',
    },
    btnRed: { background: 'linear-gradient(90deg,#f43f5e,#e11d48)', color: 'white' },
    btnGray: { background: 'rgba(255,255,255,0.14)', color: 'white' },
    btnTeal: { background: 'linear-gradient(90deg,#14b8a6,#0d9488)', color: 'white' },
    btnBlue: { background: 'linear-gradient(90deg,#2563eb,#1d4ed8)', color: 'white' },
    savedWrap: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: '10px', padding: '24px 16px',
    },
    doneCheck: {
        width: '56px', height: '56px', borderRadius: '50%', background: '#14b8a6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '28px', color: 'white', fontWeight: '700',
    },
    savedLabel: { color: 'white', fontWeight: '600', fontSize: '14px', margin: 0, fontFamily: 'sans-serif' },
};